import React from 'react';
import { Plugins, Match } from '@leverege/plugin';
import { Route, Redirect } from 'react-router';
import { History } from '@leverege/molten-core';
const filterEmpty = e => e.length > 0;
const stringify = s => {
  return s[0] === ':' ? 'C' : 'A';
};
let id = 1;

/**
 * Takes a route plugin and turns it into a node used for sorting.
 * We are going to turn the routes into strings of 'A's, 'C's, 'E's and 'R's, 
 * where 'A' are explicit path components, and 'C's are variable (changeable)
 * parameters, 'E's are extact match and 'R's indicates the rest matches too.
 * These will then be sorted first by length, then by string sort on the ACER 
 * strings. If route.path is an array of strings, this will return an array of
 * nodes.
 */
function makeNode(route) {
  if (Array.isArray(route.path)) {
    return route.path.map(path => {
      const sort = toCode(path, route.exact || false);
      return {
        sort,
        route
      };
    });
  }
  if (typeof route.path === 'string') {
    const path = route.path.trim();
    const sort = toCode(path, route.exact || false);
    return {
      key: `rid-${id++}`,
      sort,
      route
    };
  }
  // eslint-disable-next-line no-console
  console.error('Route does not have a path', route);
  return null;
}

// A Extact path
// C Parameter path
// E Extact url
// R Start of url
// const x = [ 'AAE', 'AAR', 'ACE', 'ACR', 'AE', 'AR', 'CE', 'CR', 'CAE', 'CAR', 'CCE', 'CCR'  ]
function toCode(path, exact) {
  const tPath = path.trim();
  return tPath.split('/').filter(filterEmpty).map(stringify).join('') + (exact ? 'E' : 'R');
}

/**
 * Sorts the nodes by length of sort code, then alphabetical
 */
function sortNodes(a, b) {
  const av = a.sort;
  const bv = b.sort;
  if (av.length === bv.length) {
    return a.sort.localeCompare(b.sort);
  }
  return bv.length - av.length;
}

/**
 * Organize the routes into an easily looked up tree
 */
function processor(rts) {
  const cache = {};
  const routesFor = (key, matchContext) => {
    if (cache[key]) {
      return cache[key];
    }
    const routes = [];
    for (let n = 0; n < rts.length; n++) {
      const rt = rts[n];
      const {
        matches
      } = rt;
      if (matches == null || matches.use != null && matchContext.use == null) {
        continue;
      }
      if (Match.isMatch(matchContext, matches)) {
        routes.push(rt);
      }
    }
    cache[key] = routes.map(makeNode).filter(f => f != null).flat().sort(sortNodes);
    return cache[key];
  };
  return {
    routesFor
  };
}
const view = Plugins.createView('Route', {
  processor
});
function clientUseKey(client, use) {
  return use == null ? client : `${client}:${use}`;
}

/**
 * This will return an array of (Route) objects meant for the client.
 * If the client has multiple route viewers, the 'use' can be supplied
 * to indicate the particular use for the route. The redirect, if not null,
 * will be install as the last route incase none of the route plugins match.
 * A plugin can override this by specifying 'default' = true, but it is harder
 * to then control the default route if you want this changed.
 */
function getRoutesFor(routeKey, use, matchContext, extraProps, redirect = null) {
  const key = clientUseKey(routeKey, use);
  const {
    routesFor
  } = view.get();
  const routes = routesFor(key, matchContext);
  if (routes == null) {
    return [];
  }
  let pluginRedirect = redirect;
  const rtn = routes.map(node => {
    const {
      key,
      route
    } = node;
    const {
      exact,
      path,
      component,
      render,
      children,
      strict,
      props,
      default: isDefault
    } = route;
    if (isDefault) {
      pluginRedirect = Array.isArray(path) ? path[0] : path;
    } else if (pluginRedirect == null) {
      pluginRedirect = Array.isArray(path) ? path[0] : path;
    }
    if (component) {
      // There are some props on the Route. Use render to send it to the component
      const render = rProps => {
        return /*#__PURE__*/React.createElement(component, {
          ...props,
          ...extraProps,
          ...rProps
        }, children);
      };
      if (path === '') {
        return /*#__PURE__*/React.createElement(Route, {
          key,
          exact,
          render,
          strict
        }, children);
      }
      return /*#__PURE__*/React.createElement(Route, {
        key,
        path: History.adjustPath(path),
        exact,
        render,
        strict
      }, children);
    }
    const ovrRender = rProps => {
      return render({
        ...extraProps,
        ...rProps
      }, children);
    };
    if (path === '') {
      return /*#__PURE__*/React.createElement(Route, {
        key,
        exact,
        render: ovrRender,
        component,
        strict
      }, children);
    }
    return /*#__PURE__*/React.createElement(Route, {
      key,
      path: History.adjustPath(path),
      exact,
      render: ovrRender,
      component,
      strict
    }, children);
  });
  if (pluginRedirect) {
    const rprops = typeof pluginRedirect === 'string' ? {
      to: History.adjustPath(pluginRedirect),
      from: History.adjustPath(getFrom(pluginRedirect))
    } : pluginRedirect;
    rtn.push(/*#__PURE__*/React.createElement(Redirect, {
      key: '___redirect',
      ...rprops
    }, null));
  }
  return rtn;
}
function getFrom(path) {
  const arr = path.split('/');
  for (let n = arr.length - 1; n >= 0; n--) {
    if (arr[n][0] === ':') {
      return arr.slice(0, n + 1).join('/');
    }
  }
  return arr.slice(0, arr.length - 1).join('/');
}
export default {
  getRoutesFor
};